import React, { useState, useEffect } from 'react';
import {
  FiPlus, FiCheck, FiX, FiCheckCircle,
  FiAlertCircle, FiBox, FiLink, FiCopy, FiUser,
  FiEdit2, FiTrash2
} from 'react-icons/fi';
import { partnerService, industryService, courseCategoryService } from '../services/supabaseClient';
import '../styles/Partners.css';

// UI keeps friendly type keys; DB (partners_partner_type_check) needs exact strings.
const PARTNER_TYPES = {
  integration: {
    label: 'System Integration Partner',
    dbValue: 'system_integration_partner',
    desc: 'For software reselling and professional services organisations.',
    categoryNames: ['Integration', 'Workflow Automation']
  },
  tech: {
    label: 'Technology Partner',
    dbValue: 'technology_partner',
    desc: 'For software companies embedding AI orchestration.',
    categoryNames: ['Agentic AI', 'Workflow Automation', 'Integration']
  },
  education: {
    label: 'Education Partner',
    dbValue: 'education_partner',
    desc: 'For institutions training AI to the next generation of professionals.',
    categoryNames: ['Agentic AI', 'Foundations']
  }
};

const blankForm = {
  firstName: '',
  lastName: '',
  email: '',
  mobile: '',
  company: '',
  industryId: '',
  partnerTypeKey: 'integration',
  password: ''
};

// reverse-lookup: db partner_type value -> UI key, for pre-filling the edit form
const typeKeyFromDbValue = (dbValue) =>
  Object.keys(PARTNER_TYPES).find(key => PARTNER_TYPES[key].dbValue === dbValue) || 'integration';

export default function Partners() {
  const [rows, setRows] = useState([]);
  const [rowsLoading, setRowsLoading] = useState(true);
  const [rowsError, setRowsError] = useState('');
  const [industries, setIndustries] = useState([]);
  const [categories, setCategories] = useState([]);

  const [isAddOpen, setIsAddOpen] = useState(false);
  const [isInviteOpen, setIsInviteOpen] = useState(false);
  const [form, setForm] = useState(blankForm);
  const [agreementFile, setAgreementFile] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  // null = "Add Partner" mode. Any other value = editing that partner_id.
  const [editingPartnerId, setEditingPartnerId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  // DB CONNECTED: load partners + the two lookup lists (industries, categories) on mount
  const loadPartners = () => {
    setRowsLoading(true);
    return partnerService.getAll()
      .then(data => setRows(data))
      .catch(() => setRowsError('Could not load partners.'))
      .finally(() => setRowsLoading(false));
  };

  useEffect(() => {
    let cancelled = false;

    loadPartners();

    industryService.getAll()
      .then(rows => { if (!cancelled) setIndustries(rows); })
      .catch(() => { if (!cancelled) setIndustries([]); });

    courseCategoryService.getAll()
      .then(rows => { if (!cancelled) setCategories(rows); })
      .catch(() => { if (!cancelled) setCategories([]); });

    return () => { cancelled = true; };
  }, []);

  // locks page scroll behind either modal so only the modal body scrolls
  useEffect(() => {
    document.body.style.overflow = (isAddOpen || isInviteOpen) ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isAddOpen, isInviteOpen]);

  const resetForm = () => {
    setForm({ ...blankForm, industryId: industries[0]?.industry_id ?? '' });
    setAgreementFile(null);
  };

  const openAddModal = () => {
    setEditingPartnerId(null);
    resetForm();
    setIsAddOpen(true);
  };

  // pre-fills the same modal/form with an existing partner's data.
  // password is left blank on purpose — leaving it blank on submit means
  // "keep the current password", typing a new one means "change it".
  const openEditModal = (row) => {
    setEditingPartnerId(row.partner_id);
    setForm({
      firstName: row.first_name || '',
      lastName: row.last_name || '',
      email: row.email || '',
      mobile: row.phone || '',
      company: row.company_name || '',
      industryId: row.primary_industry_id || '',
      partnerTypeKey: typeKeyFromDbValue(row.partner_type),
      password: ''
    });
    setAgreementFile(null);
    setIsAddOpen(true);
  };

  // resolves the selected partner type's category NAMES against the real
  // course_categories loaded from the DB, so we insert real category_ids
  const resolveCategoryIds = (typeKey) => {
    const wantedNames = PARTNER_TYPES[typeKey].categoryNames.map(n => n.toLowerCase());
    return categories
      .filter(cat => wantedNames.includes(cat.name.toLowerCase()))
      .map(cat => cat.category_id);
  };

  // DB CONNECTED: create partner -> link category permissions -> (optional) upload agreement
  // Agreement is OPTIONAL — partner is created as 'active' right away and
  // logs in with email + password.
  const handleCreatePartner = async () => {
    const partnerType = PARTNER_TYPES[form.partnerTypeKey];

    const created = await partnerService.add({
      partner_code: `WZ-P-${String(rows.length + 1).padStart(4, '0')}`,
      first_name: form.firstName,
      last_name: form.lastName,
      email: form.email,
      phone: form.mobile || null,
      company_name: form.company,
      primary_industry_id: form.industryId || null,
      partner_type: partnerType.dbValue,
      password: form.password,
      status: 'active'
    });

    const categoryIds = resolveCategoryIds(form.partnerTypeKey);
    if (categoryIds.length) {
      await partnerService.setCategoryPermissions(created.partner_id, categoryIds);
    }

    if (agreementFile) {
      await partnerService.uploadAgreement(created.partner_id, agreementFile);
    }
  };

  // DB CONNECTED: update an existing partner. Password only included in the
  // payload if the admin actually typed a new one.
  const handleUpdatePartner = async () => {
    const partnerType = PARTNER_TYPES[form.partnerTypeKey];

    const payload = {
      first_name: form.firstName,
      last_name: form.lastName,
      email: form.email,
      phone: form.mobile || null,
      company_name: form.company,
      primary_industry_id: form.industryId || null,
      partner_type: partnerType.dbValue
    };
    if (form.password) payload.password = form.password;

    await partnerService.update(editingPartnerId, payload);

    if (agreementFile) {
      await partnerService.uploadAgreement(editingPartnerId, agreementFile);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setRowsError('');

    const passwordRequired = !editingPartnerId; // required only when creating
    if (!form.firstName || !form.lastName || !form.email || !form.company || (passwordRequired && !form.password)) {
      setRowsError('First name, last name, email, company and password are required.');
      return;
    }

    setIsSaving(true);
    try {
      if (editingPartnerId) {
        await handleUpdatePartner();
      } else {
        await handleCreatePartner();
      }
      await loadPartners(); // simplest way to get fully joined data back
      setIsAddOpen(false);
      setEditingPartnerId(null);
    } catch (err) {
      setRowsError(err.message || 'Could not save partner.');
    } finally {
      setIsSaving(false);
    }
  };

  // DB CONNECTED: delete a partner row
  const handleDelete = async (row) => {
    const label = row.company_name || `${row.first_name || ''} ${row.last_name || ''}`.trim() || 'this partner';
    if (!window.confirm(`Delete ${label}? This cannot be undone.`)) return;

    setDeletingId(row.partner_id);
    setRowsError('');
    try {
      await partnerService.remove(row.partner_id);
      await loadPartners();
    } catch (err) {
      setRowsError(err.message || 'Could not delete partner.');
    } finally {
      setDeletingId(null);
    }
  };

  // safe against null/undefined partner_type (e.g. old rows created before
  // the partner_type constraint existed) — never returns null/undefined to JSX
  const typeLabel = (dbValue) => {
    if (!dbValue) return '—';
    return Object.values(PARTNER_TYPES).find(t => t.dbValue === dbValue)?.label || dbValue;
  };

  return (
    <div className="partner-page">
      {/* 1. HERO SECTION */}
      <div className="partner-hero">
        <div>
          <span className="eyebrow" style={{ color: 'var(--color-primary)' }}>PARTNERS</span>
          <h2>Partner Directory</h2>
          <p>Partner Type drives which categories a partner may create content in.</p>
        </div>
        <button className="primary" onClick={openAddModal}>
          <FiPlus style={{ marginRight: 6 }} /> Add Partner
        </button>
      </div>

      {/* 2. TABLE SECTION */}
      <div className="partner-panel">
        {rowsError && <div className="error">{rowsError}</div>}
        <div className="partner-table">
          <table>
            <thead>
              <tr>
                <th>ID</th><th>COMPANY</th><th>CONTACT</th><th>TYPE</th>
                <th>AGREEMENT</th>
                <th>STATUS</th><th style={{ textAlign: 'right' }}>ACTION</th>
              </tr>
            </thead>
            <tbody>
              {rowsLoading && (
                <tr><td colSpan={8} className="empty">Loading partners…</td></tr>
              )}
              {!rowsLoading && rows.map(row => (
                <tr key={row.partner_id}>
                  <td className="mono" style={{ opacity: 0.5 }}>{row.partner_code}</td>
                  <td>
                    <b style={{ color: 'var(--text-primary)', fontSize: '12px' }}>{row.company_name}</b>
                    <div style={{ fontSize: '9px', color: 'var(--text-muted)' }}>{row.primary_industries?.name || '—'}</div>
                  </td>
                  <td>
                    <b style={{ color: 'var(--text-primary)', fontSize: '11px' }}>
                      {row.first_name} {row.last_name}
                    </b>
                    <div style={{ fontSize: '9px', color: 'var(--text-muted)' }}>{row.email}</div>
                  </td>
                  <td style={{ fontSize: '11px' }}>{typeLabel(row.partner_type)}</td>
                  <td>
                    {row.agreement_file_url
                      ? <FiCheckCircle color="var(--color-success)" size={14} title="Agreement uploaded" />
                      : <FiAlertCircle color="var(--color-warning, #f59e0b)" size={14} title="Needs agreement" />}
                  </td>
                  <td>
                    <span className={`status ${row.status === 'active' ? 'success' : ''}`} style={{ fontSize: '8px', fontWeight: 900 }}>
                      {row.status === 'active' ? 'ACTIVE' : row.status === 'inactive' ? 'INACTIVE' : 'NEEDS AGREEMENT'}
                    </span>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                      <FiLink
                        size={14}
                        title="Invite link"
                        style={{ cursor: 'pointer', color: 'var(--text-muted)' }}
                        onClick={() => setIsInviteOpen(true)}
                      />
                      <FiEdit2
                        size={14}
                        title="Edit"
                        style={{ cursor: 'pointer', color: 'var(--text-muted)' }}
                        onClick={() => openEditModal(row)}
                      />
                      <FiTrash2
                        size={14}
                        title="Delete"
                        style={{
                          cursor: deletingId === row.partner_id ? 'not-allowed' : 'pointer',
                          color: 'var(--color-danger, #ef4444)',
                          opacity: deletingId === row.partner_id ? 0.5 : 1
                        }}
                        onClick={() => deletingId !== row.partner_id && handleDelete(row)}
                      />
                    </div>
                  </td>
                </tr>
              ))}
              {!rowsLoading && !rows.length && (
                <tr><td colSpan={8} className="empty">No partners yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* 3. MODAL: ADD / EDIT PARTNER */}
      {isAddOpen && (
        <div className="overlay-fixed">
          <div className="modal-container">
            <div className="modal-head">
              <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                <div style={{ width: '36px', height: '36px', background: 'var(--color-primary-lightest)', color: 'var(--color-primary)', display: 'grid', placeItems: 'center', borderRadius: '8px' }}><FiBox size={18} /></div>
                <div>
                  <h2 style={{ fontSize: '15px', margin: 0 }}>{editingPartnerId ? 'Edit Partner' : 'Add Partner'}</h2>
                  <p style={{ fontSize: '10px', color: 'var(--text-muted)', margin: 0 }}>
                    {editingPartnerId ? 'Update this partner\u2019s details.' : 'Onboard a new partner organization.'}
                  </p>
                </div>
              </div>
              <FiX style={{ cursor: 'pointer', color: 'var(--text-muted)' }} onClick={() => { setIsAddOpen(false); setEditingPartnerId(null); }} size={20} />
            </div>

            <form onSubmit={handleSubmit} className="modal-form-flex">
              <div className="modal-body-scroll">
                <span className="eyebrow" style={{ color: 'var(--color-primary)', display: 'block', marginBottom: '15px' }}>CONTACT</span>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
                  <label><span className="form-label">First name *</span>
                    <input className="modal-input-themed" placeholder="John" value={form.firstName} onChange={e => setForm({ ...form, firstName: e.target.value })} required />
                  </label>
                  <label><span className="form-label">Last name *</span>
                    <input className="modal-input-themed" placeholder="Doe" value={form.lastName} onChange={e => setForm({ ...form, lastName: e.target.value })} required />
                  </label>
                  <label><span className="form-label">Email *</span>
                    <input className="modal-input-themed" type="email" placeholder="john@company.com" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} required />
                  </label>
                  <label><span className="form-label">Mobile</span>
                    <input className="modal-input-themed" placeholder="9876543210" value={form.mobile} onChange={e => setForm({ ...form, mobile: e.target.value })} />
                  </label>
                  <label><span className="form-label">Company *</span>
                    <input className="modal-input-themed" placeholder="Company name" value={form.company} onChange={e => setForm({ ...form, company: e.target.value })} required />
                  </label>
                  <label>
                    <span className="form-label">{editingPartnerId ? 'Password (leave blank to keep current)' : 'Password *'}</span>
                    <input
                      className="modal-input-themed"
                      type="password"
                      placeholder="••••••••"
                      value={form.password}
                      onChange={e => setForm({ ...form, password: e.target.value })}
                      required={!editingPartnerId}
                    />
                  </label>
                </div>
                <label><span className="form-label">Primary industry</span>
                  <select className="modal-input-themed" value={form.industryId} onChange={e => setForm({ ...form, industryId: e.target.value })}>
                    {industries.length === 0 && <option value="">No industries yet</option>}
                    {industries.map(ind => (
                      <option key={ind.industry_id} value={ind.industry_id}>{ind.name}</option>
                    ))}
                  </select>
                </label>

                <span className="eyebrow" style={{ color: 'var(--color-primary)', display: 'block', margin: '25px 0 15px' }}>PARTNER TYPE — SETS ELIGIBILITY</span>

                {Object.entries(PARTNER_TYPES).map(([key, type]) => (
                  <div
                    key={key}
                    className={`partner-type-card ${form.partnerTypeKey === key ? 'active' : ''}`}
                    onClick={() => setForm({ ...form, partnerTypeKey: key })}
                  >
                    {form.partnerTypeKey === key && <FiCheck className="type-check-icon" />}
                    <span className="card-title-text">{type.label}</span>
                    <p className="card-desc-text">{type.desc}</p>
                    <div className="cat-chips-row">
                      {type.categoryNames.map(name => (
                        <span key={name} className="cat-chip-themed">{name.toUpperCase()}</span>
                      ))}
                    </div>
                  </div>
                ))}

                <span className="eyebrow" style={{ color: 'var(--color-primary)', display: 'block', margin: '25px 0 15px' }}>AGREEMENT — OPTIONAL</span>
                <div className="agreement-warning-alert">
                  <b>Agreement is optional</b>
                  <p>Partner can log in with email + password right away. You can upload the signed PDF now or later.</p>
                </div>
                <label><span className="form-label">Signed agreement (PDF)</span>
                  <input
                    type="file"
                    accept="application/pdf"
                    className="modal-input-themed"
                    style={{ padding: '8px' }}
                    onChange={e => setAgreementFile(e.target.files?.[0] || null)}
                  />
                </label>
              </div>

              <div className="modal-foot">
                <button type="button" className="secondary" onClick={() => { setIsAddOpen(false); setEditingPartnerId(null); }}>Cancel</button>
                <button type="submit" className="primary" style={{ background: 'var(--color-primary)' }} disabled={isSaving}>
                  {isSaving ? 'Saving…' : editingPartnerId ? 'Save changes' : 'Create partner'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 4. MODAL: INVITE LINK */}
      {isInviteOpen && (
        <div className="overlay-fixed">
          <div className="modal-container" style={{ width: '520px' }}>
            <div className="modal-head">
              <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                <div style={{ width: '36px', height: '36px', background: 'rgba(var(--color-primary-rgb), 0.1)', color: 'var(--color-primary)', display: 'grid', placeItems: 'center', borderRadius: '8px' }}><FiLink size={18} /></div>
                <div><h2 style={{ fontSize: '15px', margin: 0 }}>Partner Invite Link</h2><p style={{ fontSize: '10px', color: 'var(--text-muted)', margin: 0 }}>Invite partner to set password.</p></div>
              </div>
              <FiX style={{ cursor: 'pointer', color: 'var(--text-muted)' }} onClick={() => setIsInviteOpen(false)} />
            </div>
            <div className="modal-body-scroll">
              <div className="invite-success-alert">No admin-typed passwords. The link forces a first-login reset.</div>
              <label><span className="form-label">Invite URL</span>
                <div style={{ position: 'relative' }}>
                  <input className="modal-input-themed" readOnly value="https://aiacademy.wiiz.it/invite/347R9J9BDQQAYKV9" style={{ paddingRight: '40px' }} />
                  <FiCopy style={{ position: 'absolute', right: '14px', top: '12px', color: 'var(--text-muted)', cursor: 'pointer' }} />
                </div>
              </label>
            </div>
            <div className="modal-foot"><button className="secondary" onClick={() => setIsInviteOpen(false)}>Cancel</button></div>
          </div>
        </div>
      )}
    </div>
  );
}